// src/AdminOrdersList.jsx
import React, { useEffect, useMemo, useState } from "react";
import { db } from "./firebase";
import {
  collection,
  getDocs,
  query,
  orderBy,
  doc,
  updateDoc,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { useTheme } from "./contexts/ThemeContext";

export default function AdminOrdersList() {
  const { darkMode } = useTheme();
  const navigate = useNavigate();

  const [orders, setOrders] = useState([]);
  const [statusFilter, setStatusFilter] = useState("All");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const PAGE_SIZE = 10;
  const [page, setPage] = useState(1);

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadOrders();
  }, []);

  async function loadOrders() {
    setLoading(true);
    try {
      const q = query(collection(db, "orders"), orderBy("createdAt", "desc"));
      const snap = await getDocs(q);

      const list = snap.docs.map((d) => {
        const data = d.data();
        const createdAt =
          data.createdAt?.toDate?.() ||
          (data.createdAt ? new Date(data.createdAt) : null);

        return {
          id: d.id,
          orderNumber: data.orderNumber || `BB-${d.id.slice(0, 8)}`,
          createdAt,
          customer: data.customer || {},
          items: data.items || [],
          total: data.total ?? 0,
          shippingCharge: data.shippingCharge ?? 0,
          grandTotal: data.grandTotal ?? data.total ?? 0,
          status: data.status || "Pending",
        };
      });

      setOrders(list);
    } catch (err) {
      console.error("Error loading orders:", err);
    } finally {
      setLoading(false);
    }
  }

  // ---------------------- FILTER LOGIC ----------------------
  const filtered = useMemo(() => {
    let data = [...orders];

    if (statusFilter !== "All") {
      data = data.filter((o) => o.status === statusFilter);
    }

    if (startDate) {
      const s = new Date(startDate);
      s.setHours(0, 0, 0, 0);
      data = data.filter((o) => o.createdAt && o.createdAt >= s);
    }

    if (endDate) {
      const e = new Date(endDate);
      e.setHours(23, 59, 59, 999);
      data = data.filter((o) => o.createdAt && o.createdAt <= e);
    }

    if (searchTerm.trim() !== "") {
      const q = searchTerm.toLowerCase();
      data = data.filter((o) => {
        const name = (o.customer.name || "").toLowerCase();
        const mobile = (o.customer.mobile || "").toLowerCase();
        const orderNo = (o.orderNumber || "").toLowerCase();
        return (
          name.includes(q) || mobile.includes(q) || orderNo.includes(q)
        );
      });
    }

    return data;
  }, [orders, statusFilter, startDate, endDate, searchTerm]);

  // ---------------------- PAGINATION ----------------------
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));

  const paginated = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);

  // ---------------------- EXPORT CSV ----------------------
  function exportCSV() {
    if (filtered.length === 0) {
      alert("No data available for current filters.");
      return;
    }

    const headers = [
      "Order Number",
      "Date",
      "Customer Name",
      "Email",
      "Mobile",
      "Address",
      "Subtotal",
      "Shipping",
      "Grand Total",
      "Status",
    ];

    const rows = filtered.map((o) => [
      o.orderNumber,
      o.createdAt ? o.createdAt.toLocaleString() : "",
      o.customer.name || "",
      o.customer.email || "",
      o.customer.mobile || "",
      (o.customer.address || "").replace(/\n/g, " "),
      o.total,
      o.shippingCharge,
      o.grandTotal,
      o.status,
    ]);

    const csv =
      [headers, ...rows]
        .map((row) =>
          row
            .map((cell) => `"${String(cell).replace(/"/g, '""')}"`)
            .join(",")
        )
        .join("\n");

    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = "orders.csv";
    a.click();
    URL.revokeObjectURL(url);
  }

  function clearFilters() {
    setStatusFilter("All");
    setStartDate("");
    setEndDate("");
    setSearchTerm("");
    setPage(1);
  }

  // ---------------------- INLINE STATUS UPDATE ----------------------
  async function updateStatus(id, newStatus) {
    try {
      const ref = doc(db, "orders", id);
      await updateDoc(ref, { status: newStatus });

      setOrders((prev) =>
        prev.map((o) =>
          o.id === id ? { ...o, status: newStatus } : o
        )
      );
    } catch (err) {
      console.error("Status update failed:", err);
      alert("Could not update status!");
    }
  }

  return (
    <div className={darkMode ? "dark" : ""}>
      <div className="min-h-screen p-6 bg-white dark:bg-gray-900 dark:text-gray-200">

        {/* Top Bar */}
        <div className="flex flex-col md:flex-row justify-between md:items-center gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-pink-600 dark:text-pink-300">
              Orders List
            </h1>
            <p className="text-gray-500 text-sm">
              Showing {filtered.length} results
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => navigate("/admin")}
              className="px-3 py-2 bg-gray-200 dark:bg-gray-700 rounded-md"
            >
              Back to Admin
            </button>

            <button
              onClick={exportCSV}
              className="px-3 py-2 bg-pink-600 text-white rounded-md"
            >
              Export CSV
            </button>

            <button
              onClick={clearFilters}
              className="px-3 py-2 bg-gray-100 dark:bg-gray-800 rounded-md"
            >
              Clear Filters
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-6 gap-3 mb-6">
          <div className="md:col-span-2">
            <input
              type="text"
              placeholder="Search name, order #, mobile"
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
              className="w-full md:w-1/2 p-2 rounded-lg bg-gray-100 dark:bg-gray-800"
            />
          </div>
          <div>
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              className="w-full p-2 rounded-lg bg-gray-100 dark:bg-gray-800"
            >
              <option>All</option>
              <option>Pending</option>
              <option>Shipped</option>
              <option>Delivered</option>
              <option>Cancelled</option>
            </select>
          </div>
          <input
            type="date"
            value={startDate}
            onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
            className="w-full p-2 rounded-lg bg-gray-100 dark:bg-gray-800"
          />
          <input
            type="date"
            value={endDate}
            onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
            className="w-full p-2 rounded-lg bg-gray-100 dark:bg-gray-800"
          />
        </div>

        {/* TABLE */}
        <div className="overflow-x-auto rounded-lg border dark:border-gray-700">
          <table className="w-full text-sm">
            <thead className="bg-gray-100 dark:bg-gray-800">
              <tr>
                <th className="p-3 text-left">Order #</th>
                <th className="p-3 text-left">Customer</th>
                <th className="p-3 text-left">Date</th>
                <th className="p-3 text-left">Status</th>
                <th className="p-3 text-left">Subtotal</th>
                <th className="p-3 text-left">Shipping</th>
                <th className="p-3 text-left">Grand Total</th>
                <th className="p-3 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8" className="text-center p-6">Loading orders…</td>
                </tr>
              ) : paginated.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center p-6">No orders found</td>
                </tr>
              ) : (
                paginated.map((o) => (
                  <tr
                    key={o.id}
                    className="border-t dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800"
                  >
                    <td className="p-3 font-mono text-pink-600 dark:text-pink-300">{o.orderNumber}</td>
                    <td className="p-3">
                      <div className="font-semibold">{o.customer.name || "-"}</div>
                      <div className="text-xs text-gray-500">{o.customer.email || ""}</div>
                    </td>
                    <td className="p-3">{o.createdAt ? o.createdAt.toLocaleString() : "-"}</td>
                    <td className="p-3">
                      <select
                        value={o.status}
                        onChange={(e) => updateStatus(o.id, e.target.value)}
                        className="p-1 rounded bg-gray-100 dark:bg-gray-700"
                      >
                        <option>Pending</option>
                        <option>Shipped</option>
                        <option>Delivered</option>
                        <option>Cancelled</option>
                      </select>
                    </td>
                    <td className="p-3">₹{o.total}</td>
                    <td className="p-3">₹{o.shippingCharge}</td>
                    <td className="p-3 font-bold text-pink-600 dark:text-pink-300">₹{o.grandTotal}</td>
                    <td className="p-3">
                      <button
                        onClick={() => navigate(`/admin/order/${o.id}`)}
                        className="px-3 py-1 bg-pink-600 text-white rounded-md"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex justify-between items-center mt-4">
          <div className="text-gray-500">Page {page} of {totalPages}</div>
          <div className="flex gap-2">
            <button
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
              className={`px-3 py-2 rounded-md ${page <= 1 ? "bg-gray-200 dark:bg-gray-800 text-gray-400" : "bg-white dark:bg-gray-700"}`}
            >
              ← Previous
            </button>
            <button
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              className={`px-3 py-2 rounded-md ${page >= totalPages ? "bg-gray-200 dark:bg-gray-800 text-gray-400" : "bg-white dark:bg-gray-700"}`}
            >
              Next →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
